import { useState, useRef, useCallback, useEffect } from 'react';
import { Photo } from '../types';
import ThumbnailCard from './ThumbnailCard.tsx';
import { groupPhotosByMonth } from '../utils/dateUtils';
import './GalleryGrid.css';

interface GalleryGridProps {
  photos: Photo[];
  loading: boolean;
  error: string | null;
  onPhotoClick: (photo: Photo) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onRetry?: () => void;
}

const GalleryGrid = ({
  photos,
  loading,
  error,
  onPhotoClick,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onRetry,
}: GalleryGridProps) => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Handle infinite scroll
  const lastPhotoElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && onLoadMore) {
          onLoadMore();
        }
      });
      
      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMore, hasMore, onLoadMore]
  );

  // Set up intersection observer for load more trigger
  useEffect(() => {
    if (!loadMoreRef.current || !onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loading, loadingMore]);

  const handleImageError = useCallback((photoId: string) => {
    setFailedImages(prev => new Set(prev).add(photoId));
  }, []);

  const handleImageRetry = useCallback((photoId: string) => {
    setFailedImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(photoId);
      return newSet;
    });
  }, []);

  if (loading && photos.length === 0) {
    return (
      <div className="gallery-grid-loading">
        <div className="loading-spinner"></div>
        <p>Loading photos...</p>
      </div>
    );
  }

  if (error && photos.length === 0) {
    return (
      <div className="gallery-grid-error">
        <div className="error-icon">⚠️</div>
        <h3>Failed to load photos</h3>
        <p>{error}</p>
        {onRetry && (
          <button 
            onClick={onRetry}
            className="retry-button"
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="gallery-grid-empty">
        <div className="empty-icon">📷</div>
        <h3>No photos found</h3>
        <p>Try adjusting your search criteria or check back later.</p>
      </div>
    );
  }

  // Group photos by month/year
  const photoGroups = groupPhotosByMonth(photos);

  return (
    <div className="gallery-grid-container">
      {photoGroups.map((group, groupIndex) => (
        <div key={group.monthYear} className="photo-group">
          <div className="month-separator">
            <h3 className="month-title">{group.displayDate}</h3>
            <div className="month-line"></div>
          </div>
          
          <div className="gallery-grid">
            {group.photos.map((photo, photoIndex) => {
              const globalIndex = photoGroups
                .slice(0, groupIndex)
                .reduce((acc, g) => acc + g.photos.length, 0) + photoIndex;
              const isLast = globalIndex === photos.length - 1;
              const hasFailed = failedImages.has(photo.id);
              
              return (
                <div
                  key={photo.id}
                  ref={isLast ? lastPhotoElementRef : null}
                  className="gallery-grid-item"
                >
                  <ThumbnailCard
                    photo={photo}
                    onClick={() => onPhotoClick(photo)}
                    onError={() => handleImageError(photo.id)}
                    onRetry={() => handleImageRetry(photo.id)}
                    hasFailed={hasFailed}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Load more trigger for infinite scroll */}
      {hasMore && (
        <div ref={loadMoreRef} className="load-more-trigger">
          {loadingMore && (
            <div className="loading-more">
              <div className="loading-spinner small"></div>
              <p>Loading more photos...</p>
            </div>
          )}
        </div>
      )}

      {/* Error state for additional loading */}
      {error && photos.length > 0 && (
        <div className="gallery-grid-load-error">
          <p>Failed to load more photos: {error}</p>
        </div>
      )}
    </div>
  );
};

export default GalleryGrid;